import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SearchableDropdown from '../../common/SearchableDropdown';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack, TrendingUp, TrendingDown, FilterList } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// ─── Theme ──────────────────────────────────────────────────────────────────
const PRIMARY      = '#1a237e';
const PRIMARY_MID  = '#283593';
const PRIMARY_DARK = '#0d1b6e';
const ACCENT_GREEN = '#1b5e20';
const ACCENT_RED   = '#b71c1c';

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmt2Date = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
};
const fmtDate = (s) => {
  if (!s) return '-';
  const d = new Date(s);
  if (isNaN(d)) return '-';
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
};
const fmtN  = (n, dec=2) => (parseFloat(n)||0).toLocaleString('en-IN', {minimumFractionDigits:dec, maximumFractionDigits:dec});
const fmtAmt = (n) => `₹${fmtN(n)}`;
const pct    = (n) => `${(parseFloat(n)||0).toFixed(2)}%`;

const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius:'8px',
  height:29, width:29, color:'#fff', flexShrink:0,
  '&:hover':{ background:'rgba(255,255,255,0.15)' },
  '&.Mui-disabled':{ color:'rgba(255,255,255,0.3)' },
};

// ─── Column definitions ─────────────────────────────────────────────────────
const COLUMNS = [
  { key:'#',               label:'#',              align:'center', width:36  },
  { key:'BillDate',        label:'Bill Date',      align:'left',  width:90  },
  { key:'InvoiceNo',       label:'Invoice No',     align:'left',  width:90  },
  { key:'ItemCode',        label:'Item Code',      align:'left',  width:90  },
  { key:'ItemName',        label:'Item Name',      align:'left',  width:190 },
  { key:'Quantity',        label:'Qty',            align:'right', width:70  },
  { key:'SaleRate',        label:'Sale Rate',      align:'right', width:95  },
  { key:'PurchaseRate',    label:'Purch. Rate',    align:'right', width:95  },
  { key:'SaleAmount',      label:'Sale Amt',       align:'right', width:105 },
  { key:'CostAmount',      label:'Cost Amt',       align:'right', width:105 },
  { key:'GrossProfit',     label:'Gross Profit',   align:'right', width:110 },
  { key:'ProfitPercentage',label:'Profit %',       align:'right', width:80  },
  { key:'CGSTAmount',      label:'CGST',           align:'right', width:85  },
  { key:'SGSTAmount',      label:'SGST',           align:'right', width:85  },
  { key:'IGSTAmount',      label:'IGST',           align:'right', width:85  },
  { key:'InvoiceValue',    label:'Invoice Value',  align:'right', width:115 },
  { key:'CustomerName',    label:'Customer',       align:'left',  width:160 },
  { key:'BatchNumber',     label:'Batch No',       align:'left',  width:90  },
  { key:'SalesmanName',    label:'Salesman',       align:'left',  width:130 },
  { key:'CategoryName',    label:'Category',       align:'left',  width:120 },
  { key:'BrandName',       label:'Brand',          align:'left',  width:110 },
];

// ─── Main Component ──────────────────────────────────────────────────────────
const DailyProfitReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const today = new Date();
  const [fromDate,    setFromDate]    = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate,      setToDate]      = useState(fmt2Date(today));
  const [data,        setData]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [fetched,     setFetched]     = useState(false);
  const [search,      setSearch]      = useState('');

  // ── Dropdown lists ──
  const [branches,    setBranches]    = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [brands,      setBrands]      = useState([]);
  const [allItems,    setAllItems]    = useState([]);
  const [salesmen,    setSalesmen]    = useState([]);

  // ── Selected filters ──
  const [selBranch,   setSelBranch]   = useState('');   // will be set to branchId once loaded
  const [selCategory, setSelCategory] = useState('');
  const [selBrand,    setSelBrand]    = useState('');
  const [selItem,     setSelItem]     = useState('');
  const [selSalesman, setSelSalesman] = useState('');

  // ── tmpType ──  e.g. 'all'
  const tmpType = 'DailyProfitReport';

  // ─── Load master dropdowns ─────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId || !token) return;
    Promise.allSettled([
      fetchData('BranchApi',         `/${companyId}`,          token),
      fetchData('CategoryApi',       `/${companyId}`,          token),
      fetchData('BrandApi',          `/${companyId}`,          token),
      fetchData('ItemApi',           `/${companyId}`,          token),
      fetchData('SalesExecutiveApi', `/${companyId}`,          token),
    ]).then(([brRes, catRes, brndRes, itemRes, smRes]) => {
      if (brRes.status   === 'fulfilled') setBranches   (brRes.value?.response_data   || brRes.value   || []);
      if (catRes.status  === 'fulfilled') setCategories (catRes.value?.response_data  || catRes.value  || []);
      if (brndRes.status === 'fulfilled') setBrands     (brndRes.value?.response_data || brndRes.value || []);
      if (itemRes.status === 'fulfilled') setAllItems   (itemRes.value?.response_data || itemRes.value || []);
      if (smRes.status   === 'fulfilled') setSalesmen   (smRes.value?.response_data   || smRes.value   || []);
    });
  }, [companyId, token]);

  // ── Set default branch to logged-in branch ──────────────────────────────────
  useEffect(() => {
    if (branchId && !selBranch) setSelBranch(String(branchId));
  }, [branchId]); // eslint-disable-line

  // ── Filtered item list: filter by brand + category when selected ────────────
  const filteredItems = useMemo(() => {
    let items = allItems;
    if (selCategory) items = items.filter(i => String(i.CategoryId) === String(selCategory));
    if (selBrand)    items = items.filter(i => String(i.BrandId)    === String(selBrand));
    return items;
  }, [allItems, selCategory, selBrand]);

  // If currently selected item no longer appears in filtered list, clear it
  useEffect(() => {
    if (selItem && !filteredItems.find(i => String(i.Id) === String(selItem))) {
      setSelItem('');
    }
  }, [filteredItems]); // eslint-disable-line

  // ─── Auto-fetch on auth ready ─────────────────────────────────────────────
  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  // ─── Fetch report ─────────────────────────────────────────────────────────
  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
    if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    if (!companyId || !token) { setError('Authentication error'); return; }

    const branchParam   = selBranch   ? parseInt(selBranch)   : 0;
    const categoryParam = selCategory ? parseInt(selCategory) : 0;
    const brandParam    = selBrand    ? parseInt(selBrand)    : 0;
    const itemParam     = selItem     ? parseInt(selItem)     : 0;
    const salesmanParam = selSalesman ? parseInt(selSalesman) : 0;

    setLoading(true); setError('');
    try {
      const res = await fetchData(
        'ReportApi',
        `/DailyProfitReport/${fromDate}/${toDate}/${companyId}/${branchParam}/${categoryParam}/${brandParam}/${itemParam}/${salesmanParam}`,
        token
      );
      const rows = res?.response_data ?? (Array.isArray(res) ? res : []);
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error('DailyProfitReport error:', err);
      setError(`Failed to fetch report: ${err?.response?.status ? `HTTP ${err.response.status}` : err?.message || 'Unknown error'}`);
      setFetched(true);
    } finally {
      setLoading(false);
    }
  };

  // ─── Client-side search filter ────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      (r.InvoiceNo    || '').toLowerCase().includes(q) ||
      (r.ItemName     || '').toLowerCase().includes(q) ||
      (r.CustomerName || '').toLowerCase().includes(q) ||
      (r.SalesmanName || '').toLowerCase().includes(q) ||
      (r.CategoryName || '').toLowerCase().includes(q) ||
      (r.BrandName    || '').toLowerCase().includes(q) ||
      (r.ItemCode     || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  // ─── Summary totals ───────────────────────────────────────────────────────
  const totals = useMemo(() => filtered.reduce((acc, r) => ({
    Quantity:        acc.Quantity        + (parseFloat(r.Quantity)        || 0),
    SaleAmount:      acc.SaleAmount      + (parseFloat(r.SaleAmount)      || 0),
    CostAmount:      acc.CostAmount      + (parseFloat(r.CostAmount)      || 0),
    GrossProfit:     acc.GrossProfit     + (parseFloat(r.GrossProfit)     || 0),
    CGSTAmount:      acc.CGSTAmount      + (parseFloat(r.CGSTAmount)      || 0),
    SGSTAmount:      acc.SGSTAmount      + (parseFloat(r.SGSTAmount)      || 0),
    IGSTAmount:      acc.IGSTAmount      + (parseFloat(r.IGSTAmount)      || 0),
    InvoiceValue:    acc.InvoiceValue    + (parseFloat(r.InvoiceValue)    || 0),
  }), {
    Quantity:0, SaleAmount:0, CostAmount:0, GrossProfit:0,
    CGSTAmount:0, SGSTAmount:0, IGSTAmount:0, InvoiceValue:0,
  }), [filtered]);

  const overallProfitPct = totals.SaleAmount
    ? ((totals.GrossProfit / totals.SaleAmount) * 100).toFixed(2)
    : '0.00';
  const isProfit = totals.GrossProfit >= 0;

  // ─── Group filtered rows by BranchName ───────────────────────────────────
  const groupedByBranch = useMemo(() => {
    const map = new Map();
    filtered.forEach(r => {
      const key = r.BranchName || 'Unknown Branch';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries()); // [ [branchName, rows[]], ... ]
  }, [filtered]);

  const branchSubtotal = (rows) => rows.reduce((acc, r) => ({
    Quantity:     acc.Quantity     + (parseFloat(r.Quantity)     || 0),
    SaleAmount:   acc.SaleAmount   + (parseFloat(r.SaleAmount)   || 0),
    CostAmount:   acc.CostAmount   + (parseFloat(r.CostAmount)   || 0),
    GrossProfit:  acc.GrossProfit  + (parseFloat(r.GrossProfit)  || 0),
    CGSTAmount:   acc.CGSTAmount   + (parseFloat(r.CGSTAmount)   || 0),
    SGSTAmount:   acc.SGSTAmount   + (parseFloat(r.SGSTAmount)   || 0),
    IGSTAmount:   acc.IGSTAmount   + (parseFloat(r.IGSTAmount)   || 0),
    InvoiceValue: acc.InvoiceValue + (parseFloat(r.InvoiceValue) || 0),
  }), { Quantity:0, SaleAmount:0, CostAmount:0, GrossProfit:0, CGSTAmount:0, SGSTAmount:0, IGSTAmount:0, InvoiceValue:0 });

  // ─── Excel Export ─────────────────────────────────────────────────────────
  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'DailyProfitReport', 0);
    if (!filtered.length) return;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Daily Profit Report');
    const totalCols = COLUMNS.length;
    ws.views = [{ state:'frozen', xSplit:0, ySplit:5 }];

    const thin = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };

    // Row 1 — Company
    ws.mergeCells(1,1,1,totalCols);
    const r1 = ws.getCell('A1');
    r1.value = companyName || 'Company';
    r1.font  = { bold:true, size:14, color:{argb:'FF1a237e'} };
    r1.alignment = { horizontal:'center', vertical:'middle' };
    r1.fill  = { type:'pattern', pattern:'solid', fgColor:{argb:'FFe8eaf6'} };
    ws.getRow(1).height = 22;

    // Row 2 — Title
    ws.mergeCells(2,1,2,totalCols);
    const r2 = ws.getCell('A2');
    r2.value = 'Daily Profit Report';
    r2.font  = { bold:true, size:12, color:{argb:'FF283593'} };
    r2.alignment = { horizontal:'center', vertical:'middle' };
    ws.getRow(2).height = 18;

    // Row 3 — Date range
    ws.mergeCells(3,1,3,totalCols);
    const r3 = ws.getCell('A3');
    r3.value = `Period: ${fmtDate(fromDate)} to ${fmtDate(toDate)}`;
    r3.font  = { size:10, color:{argb:'FF37474f'} };
    r3.alignment = { horizontal:'center', vertical:'middle' };

    // Row 4 — Generated by
    ws.mergeCells(4,1,4,totalCols);
    const r4 = ws.getCell('A4');
    r4.value = `Generated on: ${new Date().toLocaleString()} | By: ${username}`;
    r4.font  = { italic:true, size:9, color:{argb:'FF9e9e9e'} };
    r4.alignment = { horizontal:'center', vertical:'middle' };

    // Row 5 — Column headers
    const hdr = ws.addRow(COLUMNS.map(c => c.label));
    hdr.height = 18;
    hdr.eachCell((cell, ci) => {
      cell.font      = { bold:true, size:9, color:{argb:'FFFFFFFF'} };
      cell.fill      = { type:'pattern', pattern:'solid', fgColor:{argb:'FF1a237e'} };
      cell.alignment = { horizontal: COLUMNS[ci-1]?.align === 'right' ? 'right' : 'center', vertical:'middle' };
      cell.border    = thin;
    });

    // ── Helper: write a numeric/subtotal row ─────────────────────────────────
    const writeSubtotalRow = (sub, labelText, bgArgb, fontArgb) => {
      const subPct = sub.SaleAmount
        ? ((sub.GrossProfit / sub.SaleAmount) * 100).toFixed(2)
        : '0.00';
      const stRow = ws.addRow(COLUMNS.map(c => {
        if (c.key === '#')               return '';
        if (c.key === 'InvoiceNo')       return labelText;
        if (c.key === 'Quantity')        return parseFloat(sub.Quantity)     || 0;
        if (c.key === 'SaleAmount')      return parseFloat(sub.SaleAmount)   || 0;
        if (c.key === 'CostAmount')      return parseFloat(sub.CostAmount)   || 0;
        if (c.key === 'GrossProfit')     return parseFloat(sub.GrossProfit)  || 0;
        if (c.key === 'ProfitPercentage')return `${subPct}%`;
        if (c.key === 'CGSTAmount')      return parseFloat(sub.CGSTAmount)   || 0;
        if (c.key === 'SGSTAmount')      return parseFloat(sub.SGSTAmount)   || 0;
        if (c.key === 'IGSTAmount')      return parseFloat(sub.IGSTAmount)   || 0;
        if (c.key === 'InvoiceValue')    return parseFloat(sub.InvoiceValue) || 0;
        return '';
      }));
      stRow.eachCell((cell, ci) => {
        cell.font      = { bold:true, size:9, color:{argb:fontArgb} };
        cell.fill      = { type:'pattern', pattern:'solid', fgColor:{argb:bgArgb} };
        cell.border    = thin;
        cell.alignment = { horizontal: COLUMNS[ci-1]?.align || 'left', vertical:'middle' };
      });
    };

    // ── Branch-grouped data rows ─────────────────────────────────────────────
    let globalIdx = 0;
    groupedByBranch.forEach(([branchName, branchRows]) => {

      // Branch header row — merged across all columns
      const bHdrRow = ws.addRow(
        COLUMNS.map((_, ci) => ci === 0 ? '' : ci === 1 ? `  ${branchName}  (${branchRows.length} records)` : '')
      );
      bHdrRow.height = 16;
      bHdrRow.eachCell((cell) => {
        cell.font      = { bold:true, size:10, color:{argb:'FFFFFFFF'} };
        cell.fill      = { type:'pattern', pattern:'solid', fgColor:{argb:'FF1a237e'} };
        cell.border    = thin;
        cell.alignment = { horizontal:'left', vertical:'middle' };
      });
      if (totalCols > 2) ws.mergeCells(bHdrRow.number, 2, bHdrRow.number, totalCols);

      // Data rows
      branchRows.forEach((r, i) => {
        globalIdx++;
        const row = ws.addRow(COLUMNS.map(c => {
          if (c.key === '#')        return globalIdx;
          if (c.key === 'BillDate') return fmtDate(r.BillDate);
          return r[c.key] ?? '';
        }));
        const bg = i % 2 === 0 ? 'FFFFFFFF' : 'FFf3f4fd';
        row.eachCell((cell, ci) => {
          cell.border    = thin;
          cell.fill      = { type:'pattern', pattern:'solid', fgColor:{argb:bg} };
          cell.alignment = { horizontal: COLUMNS[ci-1]?.align || 'left', vertical:'middle' };
          cell.font      = { size:9 };
        });
      });

      // Branch subtotal
      const sub = branchSubtotal(branchRows);
      writeSubtotalRow(sub, `Subtotal — ${branchName}`, 'FFe8eaf6', 'FF1a237e');
    });

    // ── Grand total row ───────────────────────────────────────────────────────
    writeSubtotalRow(totals, 'GRAND TOTAL', 'FF0d47a1', 'FFFFFFFF');

    ws.columns = COLUMNS.map(c => ({ width: Math.max((c.width || 100) / 7, 8) }));
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `DailyProfitReport_${fromDate}_to_${toDate}.xlsx`);
  };

  // ─── Cell value renderer ──────────────────────────────────────────────────
  const renderCell = (col, row) => {
    const v = row[col.key];
    switch (col.key) {
      case 'BillDate':         return fmtDate(v);
      case 'Quantity':         return fmtN(v, 3);
      case 'SaleRate':
      case 'PurchaseRate':     return fmtAmt(v);
      case 'SaleAmount':
      case 'CostAmount':
      case 'CGSTAmount':
      case 'SGSTAmount':
      case 'IGSTAmount':
      case 'InvoiceValue':     return fmtAmt(v);
      case 'GrossProfit':
        return (
          <span style={{ color: (parseFloat(v)||0) >= 0 ? ACCENT_GREEN : ACCENT_RED, fontWeight:700 }}>
            {fmtAmt(v)}
          </span>
        );
      case 'ProfitPercentage':
        return (
          <span style={{ color: (parseFloat(v)||0) >= 0 ? ACCENT_GREEN : ACCENT_RED, fontWeight:700 }}>
            {pct(v)}
          </span>
        );
      default: return v ?? '-';
    }
  };

  // ─── Table header/cell styles ─────────────────────────────────────────────
  const thStyle = (col) => ({
    position: 'sticky', top:0, zIndex:3,
    background: PRIMARY_DARK, color:'#fff',
    fontWeight:700, fontSize:10, textTransform:'uppercase', letterSpacing:'0.4px',
    padding:'7px 8px', borderRight:'1px solid rgba(255,255,255,0.12)',
    borderBottom:'2px solid rgba(255,255,255,0.2)',
    whiteSpace:'nowrap', textAlign: col.align,
    minWidth: col.width, width: col.width,
  });
  const tdStyle = (col, i) => ({
    padding:'5px 8px', fontSize:11, textAlign: col.align,
    borderBottom:'1px solid #f1f5f9', borderRight:'1px solid #f1f5f9',
    whiteSpace:'nowrap', color:'#374151',
    background: i % 2 === 0 ? '#ffffff' : '#f3f4fd',
  });

  // ─── KPI cards data ───────────────────────────────────────────────────────
  const kpis = [
    { label:'Records',      value: filtered.length,              color:PRIMARY_MID,   bg:'#e8eaf6' },
    { label:'Sale Amt',     value: fmtAmt(totals.SaleAmount),    color:'#0277bd',     bg:'#e1f5fe' },
    { label:'Cost Amt',     value: fmtAmt(totals.CostAmount),    color:'#6a1b9a',     bg:'#f3e5f5' },
    { label:'Gross Profit', value: fmtAmt(totals.GrossProfit),   color: isProfit ? ACCENT_GREEN : ACCENT_RED, bg: isProfit ? '#e8f5e9' : '#ffebee' },
    { label:'Profit %',     value: `${overallProfitPct}%`,       color: isProfit ? ACCENT_GREEN : ACCENT_RED, bg: isProfit ? '#e8f5e9' : '#ffebee' },
    { label:'Invoice Value',value: fmtAmt(totals.InvoiceValue),  color:'#004d40',     bg:'#e0f2f1' },
    { label:'CGST',         value: fmtAmt(totals.CGSTAmount),    color:'#558b2f',     bg:'#f1f8e9' },
    { label:'SGST',         value: fmtAmt(totals.SGSTAmount),    color:'#558b2f',     bg:'#f1f8e9' },
    { label:'IGST',         value: fmtAmt(totals.IGSTAmount),    color:'#558b2f',     bg:'#f1f8e9' },
  ];

  // ─── Dropdown options builders ────────────────────────────────────────────
  const branchOpts   = [{ value:'', label:'— All Branches —' },   ...branches.map(b  => ({ value:String(b.Id),  label: b.Name || b.BranchName || '' }))];
  const categoryOpts = [{ value:'', label:'— All Categories —' }, ...categories.map(c => ({ value:String(c.Id), label: c.Name || '' }))];
  const brandOpts    = [{ value:'', label:'— All Brands —' },     ...brands.map(b     => ({ value:String(b.Id), label: b.Name || '' }))];
  const itemOpts     = [{ value:'', label:'— All Items —' },      ...filteredItems.map(i => ({ value:String(i.Id), label: i.Name ? `${i.Code ? i.Code+' – ' : ''}${i.Name}` : String(i.Id) }))];
  const salesmanOpts = [{ value:'', label:'— All Salesmen —' },   ...salesmen.map(s   => ({ value:String(s.Id), label: s.Name || '' }))];

  const [filtersOpen, setFiltersOpen] = useState(false);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight:'100%', background:'#f0f2f5', padding:10, fontFamily:"'Inter','Segoe UI',sans-serif", boxSizing:'border-box' }}>
      <LogoLoader open={loading} message="Loading Daily Profit Report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={()=>setError('')} anchorOrigin={{ vertical:'top', horizontal:'center' }}>
        <Alert severity="error" onClose={()=>setError('')} sx={{ width:'100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background:'#fff', borderRadius:10, boxShadow:'0 4px 16px rgba(0,0,0,0.09)', border:'1px solid #e2e8f0', overflow:'hidden' }}>

        {/* ── Toolbar Row 1: Title + Date + Search + Actions ── */}
        <div style={{
          display:'flex', alignItems:'center', padding:'6px 12px',
          background:`linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY} 60%, ${PRIMARY_MID} 100%)`,
          gap:6, flexWrap:'nowrap', overflowX:'auto', overflowY:'hidden', scrollbarWidth:'none',
          boxShadow:'0 2px 8px rgba(13,71,161,0.35)',
        }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={()=>history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize:16 }} />
            </IconButton>
          </Tooltip>

          <div style={{ flexShrink:0 }}>
            <div style={{ fontSize:13, fontWeight:800, color:'#fff', whiteSpace:'nowrap', letterSpacing:'0.3px' }}>Daily Profit Report</div>
            <div style={{ fontSize:9, color:'rgba(255,255,255,0.65)', whiteSpace:'nowrap' }}>Item-wise sale profitability</div>
          </div>

          <div style={{ flex:1, minWidth:4 }} />

          {/* Search */}
          <input
            placeholder="Search invoice / item / customer..."
            value={search} onChange={e=>setSearch(e.target.value)}
            style={{ height:29, border:'1px solid rgba(255,255,255,0.3)', borderRadius:8, padding:'0 10px',
              fontSize:12, backgroundColor:'rgba(255,255,255,0.1)', color:'#fff', colorScheme:'dark',
              flexShrink:0, minWidth:200, outline:'none' }}
          />

          {/* From Date */}
          <input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)}
            style={{ height:29, border:'1px solid rgba(255,255,255,0.3)', borderRadius:8, padding:'0 8px',
              fontSize:12, backgroundColor:'rgba(255,255,255,0.1)', color:'#fff', colorScheme:'dark', flexShrink:0 }} />

          {/* To Date */}
          <input type="date" value={toDate} onChange={e=>setToDate(e.target.value)}
            style={{ height:29, border:'1px solid rgba(255,255,255,0.3)', borderRadius:8, padding:'0 8px',
              fontSize:12, backgroundColor:'rgba(255,255,255,0.1)', color:'#fff', colorScheme:'dark', flexShrink:0 }} />

          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize:16 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize:16 }} />
            </IconButton>
          </Tooltip>

          {/* Filter toggle button */}
          <Tooltip title={filtersOpen ? 'Hide Filters' : 'Show Filters'} arrow>
            <IconButton
              onClick={() => setFiltersOpen(o => !o)}
              size="small"
              sx={{
                ...iconBtn,
                position: 'relative',
                background: filtersOpen ? 'rgba(255,255,255,0.22)' : 'transparent',
              }}
            >
              <FilterList sx={{ fontSize:16 }} />
              {/* Active-filter badge */}
              {[selCategory, selBrand, selItem, selSalesman].filter(Boolean).length > 0 && (
                <span style={{
                  position:'absolute', top:2, right:2,
                  width:7, height:7, borderRadius:'50%',
                  background:'#ffeb3b', border:'1px solid rgba(0,0,0,0.2)',
                }} />
              )}
            </IconButton>
          </Tooltip>
        </div>

        {/* ── Vertical Filter Panel (collapsible) ── */}
        {filtersOpen && (
          <div style={{
            background:`linear-gradient(180deg, #1a237e 0%, #283593 100%)`,
            borderBottom:'2px solid rgba(255,255,255,0.15)',
            padding:'10px 16px 12px',
          }}>
            {/* Panel title row */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
              <span style={{ fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.85)', letterSpacing:'0.5px', textTransform:'uppercase' }}>
                🔍 Filter Options
              </span>
              {[selCategory, selBrand, selItem, selSalesman, String(selBranch) !== String(branchId) ? '1' : ''].filter(Boolean).length > 0 && (
                <button
                  onClick={() => { setSelBranch(String(branchId)); setSelCategory(''); setSelBrand(''); setSelItem(''); setSelSalesman(''); }}
                  style={{
                    height:24, border:'1px solid rgba(255,255,255,0.4)', borderRadius:5,
                    background:'rgba(255,80,80,0.25)', color:'#ffcdd2', fontSize:10,
                    padding:'0 10px', cursor:'pointer', fontWeight:700,
                  }}
                >
                  ✕ Clear All
                </button>
              )}
            </div>

            {/* Filter grid — single row, 5 columns */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:'8px 10px' }}>

              {/* Branch */}
              <div>
                <div style={{ fontSize:9, color:'rgba(255,255,255,0.6)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:3 }}>Branch</div>
                <SearchableDropdown value={selBranch} onChange={v => setSelBranch(v)}
                  options={branchOpts} placeholder="— All Branches —" minWidth={0} primaryColor={PRIMARY} />
              </div>

              {/* Salesman */}
              <div>
                <div style={{ fontSize:9, color:'rgba(255,255,255,0.6)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:3 }}>Salesman</div>
                <SearchableDropdown value={selSalesman} onChange={v => setSelSalesman(v)}
                  options={salesmanOpts} placeholder="— All Salesmen —" minWidth={0} primaryColor={PRIMARY} />
              </div>

              {/* Category */}
              <div>
                <div style={{ fontSize:9, color:'rgba(255,255,255,0.6)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:3 }}>Category</div>
                <SearchableDropdown value={selCategory} onChange={v => { setSelCategory(v); setSelItem(''); }}
                  options={categoryOpts} placeholder="— All Categories —" minWidth={0} primaryColor={PRIMARY} />
              </div>

              {/* Brand */}
              <div>
                <div style={{ fontSize:9, color:'rgba(255,255,255,0.6)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:3 }}>Brand</div>
                <SearchableDropdown value={selBrand} onChange={v => { setSelBrand(v); setSelItem(''); }}
                  options={brandOpts} placeholder="— All Brands —" minWidth={0} primaryColor={PRIMARY} />
              </div>

              {/* Item */}
              <div>
                <div style={{ fontSize:9, color:'rgba(255,255,255,0.6)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:3 }}>
                  Item {(selCategory || selBrand) ? `(${filteredItems.length})` : ''}
                </div>
                <SearchableDropdown value={selItem} onChange={v => setSelItem(v)}
                  options={itemOpts}
                  placeholder={selCategory || selBrand ? `— ${filteredItems.length} items —` : '— All Items —'}
                  minWidth={0} primaryColor={PRIMARY} />
              </div>

            </div>
          </div>
        )}

        {/* ── KPI Summary Strip ── */}
        {fetched && filtered.length > 0 && (
          <div style={{
            display:'flex', flexWrap:'nowrap', gap:0,
            borderBottom:'1px solid #e8eaf6', overflowX:'auto', scrollbarWidth:'none',
          }}>
            {kpis.map((k, i) => (
              <div key={k.label} style={{
                flex:1, minWidth:90, padding:'7px 10px', textAlign:'center',
                background: k.bg, borderRight: i < kpis.length-1 ? '1px solid #e8eaf6' : 'none',
              }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:3 }}>
                  {k.label === 'Gross Profit' && (isProfit
                    ? <TrendingUp sx={{ fontSize:12, color:k.color }} />
                    : <TrendingDown sx={{ fontSize:12, color:k.color }} />
                  )}
                  <div style={{ fontSize:9, color:'#78909c', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.3px' }}>
                    {k.label}
                  </div>
                </div>
                <div style={{ fontSize:13, fontWeight:800, color:k.color, lineHeight:1.3, whiteSpace:'nowrap' }}>
                  {k.value}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Meta info row ── */}
        {fetched && (
          <div style={{ padding:'4px 14px', fontSize:11, color:'#64748b', borderBottom:'1px solid #f1f5f9', display:'flex', alignItems:'center', gap:12 }}>
            <span>{fmtDate(fromDate)} – {fmtDate(toDate)}</span>
            <span>·</span>
            <span><strong>{filtered.length}</strong> records{search && ` (filtered from ${data.length})`}</span>
            {selBranch && <><span>·</span><span>Branch: <strong>{branches.find(b=>String(b.Id)===selBranch)?.Name || selBranch}</strong></span></>}
          </div>
        )}

        {/* ── Data Table ── */}
        <div style={{
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 260px)',
          position: 'relative',
          scrollbarWidth: 'thin',
          scrollbarColor: `${PRIMARY_MID} #e8eaf6`,
        }}>
          <table style={{ borderCollapse:'collapse', fontSize:11, minWidth:2200 }}>
            <thead>
              <tr>
                {COLUMNS.map(col => (
                  <th key={col.key} style={thStyle(col)}>{col.label}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} style={{ textAlign:'center', padding:48, color:'#94a3b8', fontSize:14 }}>
                    {!fetched
                      ? 'Select filters and click Refresh to load data'
                      : 'No records found for the selected criteria'
                    }
                  </td>
                </tr>
              ) : (() => {
                  let globalIdx = 0;
                  return groupedByBranch.map(([branchName, branchRows]) => {
                    const sub = branchSubtotal(branchRows);
                    const subProfitPct = sub.SaleAmount
                      ? ((sub.GrossProfit / sub.SaleAmount) * 100).toFixed(2)
                      : '0.00';
                    const subIsProfit = sub.GrossProfit >= 0;

                    return (
                      <React.Fragment key={branchName}>

                        {/* ── Branch group header ── */}
                        <tr>
                          <td colSpan={COLUMNS.length} style={{
                            background: 'linear-gradient(90deg, #1a237e 0%, #283593 100%)',
                            color: '#fff', fontWeight: 700, fontSize: 11,
                            padding: '5px 12px', letterSpacing: '0.3px',
                            borderTop: '2px solid #3949ab',
                            borderBottom: '1px solid rgba(255,255,255,0.15)',
                          }}>
                            🏢 {branchName}
                            <span style={{
                              marginLeft: 10, fontSize: 9, fontWeight: 400,
                              background: 'rgba(255,255,255,0.18)', borderRadius: 4,
                              padding: '1px 7px', verticalAlign: 'middle',
                            }}>
                              {branchRows.length} record{branchRows.length !== 1 ? 's' : ''}
                            </span>
                          </td>
                        </tr>

                        {/* ── Branch data rows ── */}
                        {branchRows.map((row, i) => {
                          const rowIdx = globalIdx++;
                          return (
                            <tr key={`${row.SaleId}-${row.ItemId}-${rowIdx}`}
                              onMouseEnter={e => { e.currentTarget.style.background = '#eef2ff'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? '#ffffff' : '#f3f4fd'; }}
                            >
                              {COLUMNS.map(col => (
                                <td key={col.key} style={tdStyle(col, i)}>
                                  {col.key === '#' ? rowIdx + 1 : renderCell(col, row)}
                                </td>
                              ))}
                            </tr>
                          );
                        })}

                        {/* ── Branch subtotal row ── */}
                        <tr>
                          {COLUMNS.map(col => {
                            const stStyle = {
                              background: '#e8eaf6',
                              color: '#1a237e', fontWeight: 700, fontSize: 10,
                              padding: '5px 8px', whiteSpace: 'nowrap',
                              borderTop: '1px solid #c5cae9',
                              borderBottom: '2px solid #9fa8da',
                              borderRight: '1px solid #c5cae9',
                              textAlign: col.align,
                            };
                            if (col.key === '#')               return <td key={col.key} style={stStyle}></td>;
                            if (col.key === 'InvoiceNo')        return <td key={col.key} style={{...stStyle, textAlign:'right', color:'#37474f'}}>Subtotal</td>;
                            if (col.key === 'Quantity')         return <td key={col.key} style={stStyle}>{fmtN(sub.Quantity, 3)}</td>;
                            if (col.key === 'SaleAmount')       return <td key={col.key} style={stStyle}>{fmtAmt(sub.SaleAmount)}</td>;
                            if (col.key === 'CostAmount')       return <td key={col.key} style={stStyle}>{fmtAmt(sub.CostAmount)}</td>;
                            if (col.key === 'GrossProfit')      return <td key={col.key} style={{...stStyle, color: subIsProfit ? ACCENT_GREEN : ACCENT_RED}}>{fmtAmt(sub.GrossProfit)}</td>;
                            if (col.key === 'ProfitPercentage') return <td key={col.key} style={{...stStyle, color: subIsProfit ? ACCENT_GREEN : ACCENT_RED}}>{subProfitPct}%</td>;
                            if (col.key === 'CGSTAmount')       return <td key={col.key} style={stStyle}>{fmtAmt(sub.CGSTAmount)}</td>;
                            if (col.key === 'SGSTAmount')       return <td key={col.key} style={stStyle}>{fmtAmt(sub.SGSTAmount)}</td>;
                            if (col.key === 'IGSTAmount')       return <td key={col.key} style={stStyle}>{fmtAmt(sub.IGSTAmount)}</td>;
                            if (col.key === 'InvoiceValue')     return <td key={col.key} style={{...stStyle, color:'#004d40'}}>{fmtAmt(sub.InvoiceValue)}</td>;
                            return <td key={col.key} style={stStyle}></td>;
                          })}
                        </tr>

                      </React.Fragment>
                    );
                  });
                })()}
            </tbody>

            {/* Sticky totals footer */}
            {filtered.length > 0 && (
              <tfoot>
                <tr>
                  {COLUMNS.map(col => {
                    const tfStyle = {
                      position:'sticky', bottom:0, zIndex:2,
                      background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY_MID} 100%)`,
                      color:'#fff', fontWeight:700, fontSize:10,
                      padding:'6px 8px', borderTop:'2px solid rgba(255,255,255,0.2)',
                      borderRight:'1px solid rgba(255,255,255,0.1)',
                      textAlign: col.align, whiteSpace:'nowrap',
                    };
                    if (col.key === '#')               return <td key={col.key} style={tfStyle}></td>;
                    if (col.key === 'ItemName')         return <td key={col.key} style={{...tfStyle, textAlign:'right', fontSize:11}}>TOTALS</td>;
                    if (col.key === 'Quantity')         return <td key={col.key} style={{...tfStyle, color:'#b3e5fc'}}>{fmtN(totals.Quantity,3)}</td>;
                    if (col.key === 'SaleAmount')       return <td key={col.key} style={{...tfStyle, color:'#b3e5fc'}}>{fmtAmt(totals.SaleAmount)}</td>;
                    if (col.key === 'CostAmount')       return <td key={col.key} style={{...tfStyle, color:'#ce93d8'}}>{fmtAmt(totals.CostAmount)}</td>;
                    if (col.key === 'GrossProfit')      return <td key={col.key} style={{...tfStyle, color: isProfit ? '#a5d6a7' : '#ef9a9a'}}>{fmtAmt(totals.GrossProfit)}</td>;
                    if (col.key === 'ProfitPercentage') return <td key={col.key} style={{...tfStyle, color: isProfit ? '#a5d6a7' : '#ef9a9a'}}>{overallProfitPct}%</td>;
                    if (col.key === 'CGSTAmount')       return <td key={col.key} style={{...tfStyle, color:'#c8e6c9'}}>{fmtAmt(totals.CGSTAmount)}</td>;
                    if (col.key === 'SGSTAmount')       return <td key={col.key} style={{...tfStyle, color:'#c8e6c9'}}>{fmtAmt(totals.SGSTAmount)}</td>;
                    if (col.key === 'IGSTAmount')       return <td key={col.key} style={{...tfStyle, color:'#c8e6c9'}}>{fmtAmt(totals.IGSTAmount)}</td>;
                    if (col.key === 'InvoiceValue')     return <td key={col.key} style={{...tfStyle, color:'#a5d6a7', fontSize:11}}>{fmtAmt(totals.InvoiceValue)}</td>;
                    return <td key={col.key} style={tfStyle}></td>;
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

      </div>
    </div>
  );
};

export default DailyProfitReport;
